'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { PrimaryButton, TextareaField } from '@/app/FormFields'

interface Props {
  ticketId: string
  context?: 'awaiting_reply' | 'follow_up'
}

const CONTEXT_COPY = {
  awaiting_reply: {
    heading: 'Reply to the expert',
    body: 'The expert has asked for more information. Please reply below and your ticket will be re-reviewed.',
    placeholder: 'Add your follow-up information here…',
    button: 'Send reply',
  },
  follow_up: {
    heading: 'Follow up with the expert',
    body: 'The expert has responded to your ticket. Use the form below if you have further questions or comments.',
    placeholder: 'Write your follow-up here…',
    button: 'Send follow-up',
  },
}

export default function FollowUpForm({ ticketId, context = 'awaiting_reply' }: Props) {
  const router = useRouter()
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const copy = CONTEXT_COPY[context]

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = text.trim()
    if (trimmed.length < 5) {
      setError('Please write at least a few words.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/tickets/${ticketId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionText: trimmed }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError((data as { error?: string }).error ?? 'Failed to send reply')
        return
      }
      setText('')
      router.refresh()
    } catch {
      setError('Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="rounded-lg border border-sky-200 bg-sky-50 p-5 dark:border-sky-400/30 dark:bg-sky-400/10">
      <h2 className="mb-1 text-sm font-semibold text-sky-950 dark:text-sky-100">
        {copy.heading}
      </h2>
      <p className="mb-4 text-sm text-sky-800 dark:text-sky-200">
        {copy.body}
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <TextareaField
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          required
          placeholder={copy.placeholder}
          className="border-sky-300 focus:border-sky-700 focus:ring-sky-700/20 dark:border-sky-500/40"
        />
        {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
        <PrimaryButton
          type="submit"
          disabled={loading || text.trim().length < 5}
        >
          {loading ? 'Sending…' : copy.button}
        </PrimaryButton>
      </form>
    </section>
  )
}
